import axiosInstance from '../../../config/axios';

export type DeltaDirection = 'up' | 'down' | 'flat';

export interface OfferCounts {
  DRAFT: number;
  SENT: number;
  ACCEPTED: number;
  REJECTED: number;
  EXPIRED: number;
  total: number;
}

export interface OfferAmounts {
  DRAFT: number;
  SENT: number;
  ACCEPTED: number;
  REJECTED: number;
  EXPIRED: number;
  total: number;
  open_pipeline: number;
}

export interface OfferMonthDelta {
  amount: number;
  percent: number | null;
  direction: DeltaDirection;
}

export interface OffersSummary {
  counts: OfferCounts;
  amounts: OfferAmounts;
  month: {
    current: { gross_total: number };
    previous: { gross_total: number };
    delta: OfferMonthDelta;
  };
  conversion_rate: number;
}

export interface InvoicingSummary {
  accepted_total_gross: number;
  invoiced_against_offers_gross: number;
  remaining_to_invoice_gross: number;
}

export interface ProjectCounts {
  LEAD: number;
  PLANNING: number;
  ACTIVE: number;
  PAUSED: number;
  DONE: number;
  total: number;
}

export interface ProjectsSummary {
  counts: ProjectCounts;
}

export interface InvoiceCounts {
  DRAFT: number;
  SENT: number;
  PAID: number;
  CANCELLED: number;
  total: number;
  overdue: number;
}

export interface InvoiceAmounts {
  DRAFT: number;
  SENT: number;
  PAID: number;
  CANCELLED: number;
  total: number;
  open: number;
  overdue: number;
}

export interface InvoiceMonthData {
  invoiced_gross: number;
  paid_gross: number;
  count: number;
}

export interface InvoiceMonthDelta {
  invoiced_amount: number;
  invoiced_percent: number | null;
  direction_invoiced: DeltaDirection;
  paid_amount: number;
  paid_percent: number | null;
  direction_paid: DeltaDirection;
}

export interface InvoiceMonthHistory {
  month: string;
  invoicedGross: number;
  paidGross: number;
  openGross: number;
  invoiceCount: number;
  paidCount: number;
}

export interface InvoiceSummary {
  counts: InvoiceCounts;
  amounts: InvoiceAmounts;
  month: {
    current: InvoiceMonthData;
    previous: InvoiceMonthData;
    delta: InvoiceMonthDelta;
  };
  history: InvoiceMonthHistory[];
}

export interface CrmTaskCounts {
  total: number;
  OPEN: number;
  IN_PROGRESS: number;
  DONE: number;
  CANCELLED: number;
}

export interface CrmTaskOverdue {
  total: number;
  by_priority?: { HIGH: number; NORMAL: number; LOW: number };
}

export interface CrmTasksSummary {
  counts: CrmTaskCounts;
  overdue: CrmTaskOverdue;
}

export interface ServiceContractsSummary {
  total_count: number;
  active_count: number;
  mrr_gross: number;
  by_interval: {
    MONTH: number;
    QUARTER: number;
    YEAR: number;
  };
}

export interface PaymentPlanSummary {
  plans_count: number;
  milestones_total: number;
  milestones_invoiced: number;
  milestones_remaining: number;
  planned_gross_total: number;
  planned_gross_invoiced: number;
  planned_gross_remaining: number;
}

export interface MilestonesCounts {
  PLANNED: number;
  READY_FOR_INVOICE: number;
  INVOICED: number;
  PAID: number;
  CANCELLED: number;
}

export interface MilestonesAmounts {
  planned: number;
  ready_for_invoice: number;
  invoiced: number;
  paid: number;
}

export interface MilestonesSummary {
  counts: MilestonesCounts;
  amounts: MilestonesAmounts;
}

export interface SalesOverview {
  offers: OffersSummary;
  projects: ProjectsSummary;
  invoices: InvoiceSummary;
  tasks: CrmTasksSummary;
  invoicing: InvoicingSummary;
  serviceContracts: ServiceContractsSummary;
  paymentPlans: PaymentPlanSummary;
  milestones: MilestonesSummary;
}

function parseAmount(value: string | number | undefined | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  return parseFloat(value) || 0;
}

function parseDirection(value: string | undefined | null): DeltaDirection {
  if (value === 'up' || value === 'down' || value === 'flat') return value;
  return 'flat';
}

export interface DeltaInfo {
  direction: DeltaDirection;
  percent: number | null;
}

export default class SalesOverviewService {
  static async getOverview(): Promise<SalesOverview> {
    const [salesResult, invoicesResult, tasksResult, contractsResult, paymentPlansResult] = await Promise.allSettled([
      axiosInstance.get('/api/admin/sales/summary/'),
      axiosInstance.get('/api/admin/invoices/summary/'),
      axiosInstance.get('/api/admin/crm-tasks/summary/'),
      axiosInstance.get('/api/admin/service-contracts/summary/'),
      axiosInstance.get('/api/admin/payment-plans/summary/'),
    ]);

    const salesRaw = salesResult.status === 'fulfilled' ? salesResult.value.data : null;
    const invoicesRaw = invoicesResult.status === 'fulfilled' ? invoicesResult.value.data : null;
    const tasksRaw = tasksResult.status === 'fulfilled' ? tasksResult.value.data : null;
    const contractsRaw = contractsResult.status === 'fulfilled' ? contractsResult.value.data : null;
    const paymentPlansRaw = paymentPlansResult.status === 'fulfilled' ? paymentPlansResult.value.data : null;

    const defaultOffers: OffersSummary = {
      counts: { DRAFT: 0, SENT: 0, ACCEPTED: 0, REJECTED: 0, EXPIRED: 0, total: 0 },
      amounts: { DRAFT: 0, SENT: 0, ACCEPTED: 0, REJECTED: 0, EXPIRED: 0, total: 0, open_pipeline: 0 },
      month: {
        current: { gross_total: 0 },
        previous: { gross_total: 0 },
        delta: { amount: 0, percent: null, direction: 'flat' },
      },
      conversion_rate: 0,
    };

    const defaultProjects: ProjectsSummary = {
      counts: { LEAD: 0, PLANNING: 0, ACTIVE: 0, PAUSED: 0, DONE: 0, total: 0 },
    };

    const defaultInvoices: InvoiceSummary = {
      counts: { DRAFT: 0, SENT: 0, PAID: 0, CANCELLED: 0, total: 0, overdue: 0 },
      amounts: { DRAFT: 0, SENT: 0, PAID: 0, CANCELLED: 0, total: 0, open: 0, overdue: 0 },
      month: {
        current: { invoiced_gross: 0, paid_gross: 0, count: 0 },
        previous: { invoiced_gross: 0, paid_gross: 0, count: 0 },
        delta: {
          invoiced_amount: 0,
          invoiced_percent: null,
          direction_invoiced: 'flat',
          paid_amount: 0,
          paid_percent: null,
          direction_paid: 'flat',
        },
      },
      history: [],
    };

    const defaultTasks: CrmTasksSummary = {
      counts: { total: 0, OPEN: 0, IN_PROGRESS: 0, DONE: 0, CANCELLED: 0 },
      overdue: { total: 0 },
    };

    const defaultServiceContracts: ServiceContractsSummary = {
      total_count: 0,
      active_count: 0,
      mrr_gross: 0,
      by_interval: { MONTH: 0, QUARTER: 0, YEAR: 0 },
    };

    const defaultPaymentPlans: PaymentPlanSummary = {
      plans_count: 0,
      milestones_total: 0,
      milestones_invoiced: 0,
      milestones_remaining: 0,
      planned_gross_total: 0,
      planned_gross_invoiced: 0,
      planned_gross_remaining: 0,
    };

    const defaultMilestones: MilestonesSummary = {
      counts: { PLANNED: 0, READY_FOR_INVOICE: 0, INVOICED: 0, PAID: 0, CANCELLED: 0 },
      amounts: { planned: 0, ready_for_invoice: 0, invoiced: 0, paid: 0 },
    };

    let offers = defaultOffers;
    if (salesRaw?.offers) {
      const o = salesRaw.offers;
      offers = {
        counts: {
          DRAFT: o.counts?.DRAFT ?? 0,
          SENT: o.counts?.SENT ?? 0,
          ACCEPTED: o.counts?.ACCEPTED ?? 0,
          REJECTED: o.counts?.REJECTED ?? 0,
          EXPIRED: o.counts?.EXPIRED ?? 0,
          total: o.counts?.total ?? 0,
        },
        amounts: {
          DRAFT: parseAmount(o.amounts?.DRAFT),
          SENT: parseAmount(o.amounts?.SENT),
          ACCEPTED: parseAmount(o.amounts?.ACCEPTED),
          REJECTED: parseAmount(o.amounts?.REJECTED),
          EXPIRED: parseAmount(o.amounts?.EXPIRED),
          total: parseAmount(o.amounts?.total),
          open_pipeline: parseAmount(o.amounts?.open_pipeline),
        },
        month: {
          current: { gross_total: parseAmount(o.month?.current?.gross_total) },
          previous: { gross_total: parseAmount(o.month?.previous?.gross_total) },
          delta: {
            amount: parseAmount(o.month?.delta?.amount),
            percent: o.month?.delta?.percent ?? null,
            direction: parseDirection(o.month?.delta?.direction),
          },
        },
        conversion_rate: SalesOverviewService.extractConversionRate(salesRaw),
      };
    }

    let projects = defaultProjects;
    if (salesRaw?.projects) {
      projects = {
        counts: {
          LEAD: salesRaw.projects.counts?.LEAD ?? 0,
          PLANNING: salesRaw.projects.counts?.PLANNING ?? 0,
          ACTIVE: salesRaw.projects.counts?.ACTIVE ?? 0,
          PAUSED: salesRaw.projects.counts?.PAUSED ?? 0,
          DONE: salesRaw.projects.counts?.DONE ?? 0,
          total: salesRaw.projects.counts?.total ?? 0,
        },
      };
    }

    let invoices = defaultInvoices;
    if (invoicesRaw) {
      const historyMonths: InvoiceMonthHistory[] = (invoicesRaw.history?.months ?? []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (m: any) => ({
          month: m.month ?? '',
          invoicedGross: parseAmount(m.invoiced_gross),
          paidGross: parseAmount(m.paid_gross),
          openGross: parseAmount(m.open_gross),
          invoiceCount: m.invoice_count ?? 0,
          paidCount: m.paid_count ?? 0,
        })
      );

      invoices = {
        counts: {
          DRAFT: invoicesRaw.counts?.DRAFT ?? 0,
          SENT: invoicesRaw.counts?.SENT ?? 0,
          PAID: invoicesRaw.counts?.PAID ?? 0,
          CANCELLED: invoicesRaw.counts?.CANCELLED ?? 0,
          total: invoicesRaw.counts?.total ?? 0,
          overdue: invoicesRaw.counts?.overdue ?? 0,
        },
        amounts: {
          DRAFT: parseAmount(invoicesRaw.amounts?.DRAFT),
          SENT: parseAmount(invoicesRaw.amounts?.SENT),
          PAID: parseAmount(invoicesRaw.amounts?.PAID),
          CANCELLED: parseAmount(invoicesRaw.amounts?.CANCELLED),
          total: parseAmount(invoicesRaw.amounts?.total),
          open: parseAmount(invoicesRaw.amounts?.open),
          overdue: parseAmount(invoicesRaw.amounts?.overdue),
        },
        month: {
          current: {
            invoiced_gross: parseAmount(invoicesRaw.month?.current?.invoiced_gross),
            paid_gross: parseAmount(invoicesRaw.month?.current?.paid_gross),
            count: invoicesRaw.month?.current?.count ?? 0,
          },
          previous: {
            invoiced_gross: parseAmount(invoicesRaw.month?.previous?.invoiced_gross),
            paid_gross: parseAmount(invoicesRaw.month?.previous?.paid_gross),
            count: invoicesRaw.month?.previous?.count ?? 0,
          },
          delta: {
            invoiced_amount: parseAmount(invoicesRaw.month?.delta?.invoiced_amount),
            invoiced_percent: invoicesRaw.month?.delta?.invoiced_percent ?? null,
            direction_invoiced: parseDirection(invoicesRaw.month?.delta?.direction_invoiced),
            paid_amount: parseAmount(invoicesRaw.month?.delta?.paid_amount),
            paid_percent: invoicesRaw.month?.delta?.paid_percent ?? null,
            direction_paid: parseDirection(invoicesRaw.month?.delta?.direction_paid),
          },
        },
        history: historyMonths,
      };
    }

    let tasks = defaultTasks;
    if (tasksRaw) {
      tasks = {
        counts: {
          total: tasksRaw.counts?.total ?? 0,
          OPEN: tasksRaw.counts?.OPEN ?? 0,
          IN_PROGRESS: tasksRaw.counts?.IN_PROGRESS ?? 0,
          DONE: tasksRaw.counts?.DONE ?? 0,
          CANCELLED: tasksRaw.counts?.CANCELLED ?? 0,
        },
        overdue: {
          total: tasksRaw.overdue?.total ?? 0,
          by_priority: tasksRaw.overdue?.by_priority,
        },
      };
    }

    const invoicing: InvoicingSummary = {
      accepted_total_gross: parseAmount(salesRaw?.invoicing?.accepted_total_gross),
      invoiced_against_offers_gross: parseAmount(salesRaw?.invoicing?.invoiced_against_offers_gross),
      remaining_to_invoice_gross: parseAmount(salesRaw?.invoicing?.remaining_to_invoice_gross),
    };

    let serviceContracts = defaultServiceContracts;
    if (contractsRaw) {
      serviceContracts = {
        total_count: contractsRaw.total_count ?? 0,
        active_count: contractsRaw.active_count ?? 0,
        mrr_gross: parseAmount(contractsRaw.mrr_gross),
        by_interval: {
          MONTH: parseAmount(contractsRaw.by_interval?.MONTH),
          QUARTER: parseAmount(contractsRaw.by_interval?.QUARTER),
          YEAR: parseAmount(contractsRaw.by_interval?.YEAR),
        },
      };
    }

    let paymentPlans = defaultPaymentPlans;
    if (paymentPlansRaw) {
      paymentPlans = {
        plans_count: paymentPlansRaw.plans_count ?? 0,
        milestones_total: paymentPlansRaw.milestones_total ?? 0,
        milestones_invoiced: paymentPlansRaw.milestones_invoiced ?? 0,
        milestones_remaining: paymentPlansRaw.milestones_remaining ?? (paymentPlansRaw.milestones_total ?? 0) - (paymentPlansRaw.milestones_invoiced ?? 0),
        planned_gross_total: parseAmount(paymentPlansRaw.planned_gross_total),
        planned_gross_invoiced: parseAmount(paymentPlansRaw.planned_gross_invoiced),
        planned_gross_remaining: parseAmount(paymentPlansRaw.planned_gross_remaining),
      };
    }

    let milestones = defaultMilestones;
    if (paymentPlansRaw?.milestones) {
      const m = paymentPlansRaw.milestones;
      milestones = {
        counts: {
          PLANNED: m.counts?.PLANNED ?? 0,
          READY_FOR_INVOICE: m.counts?.READY_FOR_INVOICE ?? 0,
          INVOICED: m.counts?.INVOICED ?? 0,
          PAID: m.counts?.PAID ?? 0,
          CANCELLED: m.counts?.CANCELLED ?? 0,
        },
        amounts: {
          planned: parseAmount(m.amounts?.planned),
          ready_for_invoice: parseAmount(m.amounts?.ready_for_invoice),
          invoiced: parseAmount(m.amounts?.invoiced),
          paid: parseAmount(m.amounts?.paid),
        },
      };
    }

    return { offers, projects, invoices, tasks, invoicing, serviceContracts, paymentPlans, milestones };
  }

  static getDeltaFromPercent(percent: number | null): DeltaInfo {
    if (percent === null || percent === 0) {
      return { direction: 'flat', percent: percent === null ? null : 0 };
    }
    return {
      direction: percent > 0 ? 'up' : 'down',
      percent: Math.abs(percent),
    };
  }

  static calculateDelta(current: number, previous: number): DeltaInfo {
    if (previous === 0) {
      if (current === 0) {
        return { direction: 'flat', percent: null };
      }
      return { direction: 'up', percent: null };
    }

    const percent = ((current - previous) / previous) * 100;

    if (Math.abs(percent) < 0.5) {
      return { direction: 'flat', percent: 0 };
    }

    return {
      direction: percent > 0 ? 'up' : 'down',
      percent: Math.abs(percent),
    };
  }

  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  static formatPercent(value: number | null): string {
    if (value === null) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static extractConversionRate(salesRaw: any): number {
    const stages = salesRaw?.charts?.pipeline_funnel?.stages;
    if (!Array.isArray(stages)) return 0;
    const acceptedStage = stages.find((s: { stage: string }) => s.stage === 'accepted');
    return acceptedStage?.conversion_rate ?? 0;
  }
}
